/**
 * User Profile interface matching the DynamoDB structure
 */
export interface UserProfile {
    userName: string;
    appUserId?: string;
    handle?: string;
    contactInfo?: any;
    onboardingCompletedAt?: string;
    profilePhotoId?: string;
}

/**
 * Centralized utility for user ID resolution and user-related operations
 * This ensures consistent behavior across the entire application
 */
export class UserUtil {
    /**
     * Get the effective user ID - the ID that should be used throughout the app
     * This is the core logic for resolving which user ID to use:
     * - If appUserId exists, use it (for linked accounts)
     * - Otherwise, fall back to userName (for regular accounts)
     * 
     * @param userProfile - User profile from database
     * @returns The effective user ID (appUserId if exists, otherwise userName)
     */
    static getEffectiveUserId(userProfile: UserProfile): string {
        return userProfile.appUserId || userProfile.userName;
    }

    /**
     * Get effective user ID from basic parameters
     * Useful when you don't have the full user profile object
     * 
     * @param userName - The Cognito username
     * @param appUserId - The linked app user ID (optional)
     * @returns The effective user ID
     */
    static getEffectiveUserIdFromBasic(userName: string, appUserId?: string): string {
        return appUserId || userName;
    }

    /**
     * Check if a user account is linked to another account
     * 
     * @param userProfile - User profile from database
     * @returns True if the account is linked (has appUserId)
     */
    static isLinkedAccount(userProfile: UserProfile): boolean {
        return !!userProfile.appUserId;
    }

    /**
     * Check if a username appears to be a Google OAuth username
     * 
     * @param userName - The username to check
     * @returns True if it looks like a Google OAuth username
     */
    static isGoogleOAuthUsername(userName: string): boolean {
        return userName.startsWith('Google_') || userName.startsWith('google_');
    }

    /**
     * Get display name for a user (prioritizes handle, falls back to effective user ID)
     * 
     * @param userProfile - User profile from database
     * @returns The best display name for the user
     */
    static getDisplayName(userProfile: UserProfile): string {
        return userProfile.handle || UserUtil.getEffectiveUserId(userProfile);
    }
}
